import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard, FilePlus, FileText, CheckSquare,
  Wallet, BarChart3, FolderOpen, Users, Settings, X
} from 'lucide-react';

const navItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/vouchers/new', label: 'Add Voucher', icon: FilePlus },
  { path: '/vouchers', label: 'All Vouchers', icon: FileText },
  { path: '/approvals', label: 'Approvals', icon: CheckSquare, roles: ['admin', 'manager'] },
  { path: '/advances', label: 'Advances', icon: Wallet },
  { path: '/reports', label: 'Reports', icon: BarChart3 },
  { path: '/categories', label: 'Categories', icon: FolderOpen, roles: ['admin'] },
  { path: '/employees', label: 'Employees', icon: Users, roles: ['admin', 'manager'] },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export default function Sidebar({ onClose }) {
  const { user } = useAuth();
  const location = useLocation();

  const filteredItems = navItems.filter(item =>
    !item.roles || item.roles.includes(user?.role)
  );

  return (
    <div className="flex flex-col h-full" data-testid="sidebar">
      {/* Logo */}
      <div className="flex items-center justify-between h-14 px-4 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-slate-900 rounded flex items-center justify-center">
            <span className="text-white text-xs font-bold">IE</span>
          </div>
          <span className="text-sm font-semibold text-slate-900 tracking-tight">IndieEyes</span>
        </div>
        <button
          onClick={onClose}
          className="lg:hidden p-1 hover:bg-slate-100 rounded"
          data-testid="sidebar-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
        {filteredItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.path === '/'
            ? location.pathname === '/'
            : location.pathname.startsWith(item.path);

          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onClose}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`
                flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium
                transition-all duration-150
                ${isActive
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}
              `}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* User info */}
      <div className="border-t border-slate-200 p-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-slate-200 rounded-full flex items-center justify-center">
            <span className="text-xs font-semibold text-slate-600">
              {user?.name?.charAt(0)?.toUpperCase()}
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-slate-900 truncate">{user?.name}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">{user?.role}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
